import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import {
  LocalRecarga, MetodoConsumo, TipoCombustivel, TIPO_COMBUSTIVEL_LABEL, TipoEnergia, UnidadeEnergia, UNIDADE_ENERGIA_LABEL, UsoTrecho, USO_TRECHO_LABEL,
} from '../../lib/enums'
import { cn, formatMoney } from '../../lib/utils'
import type { Abastecimento, SalvarAbastecimentoRequest, SalvarAbastecimentoResultado, Veiculo } from '../../types'
import { PagamentoVeiculoForm } from './PagamentoVeiculoForm'
import { montarPagamento, pagamentoValido, pagamentoVazio, type PagamentoForm } from './pagamentoUtils'
import { formatKm, hojeIso, invalidarVeiculo, lerNumero, numeroBr, previaAbastecimento, soData } from './veiculoUtils'

interface Props { open: boolean; onClose: () => void; veiculo: Veiculo; abastecimento?: Abastecimento }


/** As três opções: "Enchi" e "Reserva" valem juntas; "Parcial" é nenhuma das duas. */
export function SeletorAncora({ cheio, reserva, onChange, rotuloCheio = 'Enchi o tanque', rotuloReserva = 'Estava na reserva' }: {
  cheio: boolean; reserva: boolean; onChange: (cheio: boolean, reserva: boolean) => void; rotuloCheio?: string; rotuloReserva?: string
}) {
  const parcial = !cheio && !reserva
  const op = (ativo: boolean, rotulo: string, onClick: () => void) => (
    <button type="button" aria-pressed={ativo} onClick={onClick}
      className={cn('flex h-[38px] flex-1 items-center justify-center gap-2 rounded px-3 text-13 font-medium', ativo ? 'bg-primary/15 text-primary' : 'bg-surface2 text-muted hover:text-white')}>
      <i className={ativo ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} aria-hidden /> {rotulo}
    </button>
  )
  return (
    <div className="flex flex-wrap gap-2">
      {op(cheio, rotuloCheio, () => onChange(!cheio, reserva))}
      {op(reserva, rotuloReserva, () => onChange(cheio, !reserva))}
      {op(parcial, 'Parcial', () => onChange(false, false))}
    </div>
  )
}

export function AbastecimentoFormModal({ open, ...resto }: Props) {
  // O corpo só existe aberto, então o formulário recomeça a cada abertura.
  return open ? <Corpo {...resto} /> : null
}

function Corpo({ onClose, veiculo, abastecimento: ab }: Omit<Props, 'open'>) {
  const qc = useQueryClient()
  const toast = useToast()
  const editando = !!ab
  const soEletrico = veiculo.modelo.tipoEnergia === TipoEnergia.Eletrico
  const aceitos = veiculo.modelo.combustiveisAceitos
  const opcoes: TipoCombustivel[] = soEletrico ? [TipoCombustivel.Eletricidade] : aceitos.length ? aceitos : [TipoCombustivel.Gasolina, TipoCombustivel.Etanol]

  const [data, setData] = useState(ab ? soData(ab.data) : hojeIso())
  const [km, setKm] = useState(ab ? String(ab.odometro) : '')
  const [combustivel, setCombustivel] = useState<TipoCombustivel>(ab?.tipoCombustivel ?? opcoes[0])
  const [qtd, setQtd] = useState(ab ? String(ab.quantidade) : '')
  const [valor, setValor] = useState(ab && ab.localRecarga !== LocalRecarga.Casa ? String(ab.valorTotal) : '')
  const [postoNome, setPostoNome] = useState(ab?.postoNome ?? '')
  const [postoCnpj, setPostoCnpj] = useState(ab?.postoCnpj ?? '')
  const [cheio, setCheio] = useState(ab ? ab.tanqueCheio : veiculo.metodoPadrao === MetodoConsumo.TanqueCheio)
  const [reserva, setReserva] = useState(ab ? ab.estavaNaReserva : veiculo.metodoPadrao === MetodoConsumo.Reserva)
  const [uso, setUso] = useState<UsoTrecho | ''>(ab?.uso ?? '')
  const [casa, setCasa] = useState(ab?.localRecarga === LocalRecarga.Casa)
  const [obs, setObs] = useState(ab?.observacao ?? '')
  const [pagamento, setPagamento] = useState<PagamentoForm>(pagamentoVazio())
  const [trocarPagamento, setTrocarPagamento] = useState(!editando)
  const [alerta, setAlerta] = useState<SalvarAbastecimentoResultado | null>(null)

  const eletricidade = combustivel === TipoCombustivel.Eletricidade
  const emCasa = eletricidade && casa
  const unidade = eletricidade ? UnidadeEnergia.Kwh : combustivel === TipoCombustivel.Gnv ? UnidadeEnergia.M3 : UnidadeEnergia.L
  const rotuloUn = UNIDADE_ENERGIA_LABEL[unidade]
  const q = lerNumero(qtd) ?? 0
  const tarifa = veiculo.tarifaKwhCasa ?? 0
  const custoCasa = q * tarifa
  const total = emCasa ? custoCasa : lerNumero(valor) ?? 0
  const preco = q > 0 && total > 0 ? total / q : 0
  const previa = previaAbastecimento(cheio, reserva)
  const pagamentoOk = emCasa || (editando && !trocarPagamento) || pagamentoValido(pagamento)
  const valido = km.trim() !== '' && Number(km) >= 0 && q > 0 && (emCasa || total > 0) && !!data && pagamentoOk

  function montar(confirmar: boolean): SalvarAbastecimentoRequest {
    return {
      data, odometro: Number(km), quantidade: q, tipoCombustivel: combustivel, valorTotal: total,
      postoNome: emCasa ? null : postoNome.trim() || null, postoCnpj: emCasa ? null : postoCnpj.replace(/\D/g, '') || null,
      tanqueCheio: cheio, estavaNaReserva: reserva, uso: uso || undefined,
      localRecarga: eletricidade ? (casa ? LocalRecarga.Casa : LocalRecarga.Publica) : LocalRecarga.NaoAplica,
      observacao: obs.trim() || null,
      pagamento: emCasa || (editando && !trocarPagamento) ? null : montarPagamento(pagamento),
      confirmarRegressao: confirmar || undefined,
    }
  }

  const salvar = useMutation({
    mutationFn: (confirmar: boolean) => ab
      ? veiculosApi.abastecimentos.atualizar(veiculo.id, ab.id, montar(confirmar))
      : veiculosApi.abastecimentos.criar(veiculo.id, montar(confirmar)),
    onSuccess: (r) => {
      if (!r.gravado) { setAlerta(r); return }
      invalidarVeiculo(qc)
      toast.success(editando ? 'Abastecimento atualizado.' : 'Abastecimento registrado.')
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open onClose={onClose} size="lg" title={editando ? 'Editar abastecimento' : eletricidade ? 'Nova recarga' : 'Novo abastecimento'}
      footer={alerta
        ? <><Button variant="light" onClick={() => setAlerta(null)}>Corrigir o km</Button><Button loading={salvar.isPending} onClick={() => salvar.mutate(true)}>Gravar mesmo assim</Button></>
        : <><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate(false)}>Salvar</Button></>}
    >
      <fieldset disabled={!!alerta} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Data" type="date" max={hojeIso()} value={data} onChange={(e) => setData(e.target.value)} />
          <Input label={`Km no painel * (último: ${formatKm(veiculo.kmAtual)})`} type="number" inputMode="numeric" min={0} value={km} onChange={(e) => setKm(e.target.value)} autoFocus />
        </div>

        {opcoes.length > 1 && (
          <Select label="Combustível" value={combustivel} onChange={(e) => setCombustivel(Number(e.target.value) as TipoCombustivel)}>
            {opcoes.map((c) => <option key={c} value={c}>{TIPO_COMBUSTIVEL_LABEL[c]}</option>)}
          </Select>
        )}

        {eletricidade && (
          <div>
            <span className="form-label">Onde recarregou?</span>
            <div className="flex gap-2">
              {([[true, 'Em casa', 'ri-home-4-line'], [false, 'Eletroposto', 'ri-charging-pile-2-line']] as const).map(([v, r, i]) => (
                <button key={r} type="button" onClick={() => setCasa(v)} className={cn('inline-flex h-[38px] flex-1 items-center justify-center gap-2 rounded text-13 font-medium', casa === v ? 'bg-primary/15 text-primary' : 'bg-surface2 text-muted hover:text-white')}>
                  <i className={i} aria-hidden /> {r}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Input label={`Quantidade (${rotuloUn})`} type="number" inputMode="decimal" min={0} step="0.001" value={qtd} onChange={(e) => setQtd(e.target.value)} />
          {emCasa ? (
            <div className="sm:col-span-2">
              <span className="form-label">Custo estimado</span>
              <p className="flex h-[38px] items-center rounded border border-border/70 px-3 text-13 text-white">
                {tarifa > 0 ? `${formatMoney(custoCasa)} · ${formatMoney(tarifa)}/kWh` : 'Defina a tarifa de casa no cadastro do veículo'}
              </p>
            </div>
          ) : (
            <>
              <Input label="Valor total (R$)" type="number" inputMode="decimal" min={0} step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} />
              <div>
                <span className="form-label">Preço por {rotuloUn}</span>
                <p className="flex h-[38px] items-center rounded border border-border/70 px-3 text-13 text-white">{preco > 0 ? `${formatMoney(preco)}/${rotuloUn}` : '—'}</p>
              </div>
            </>
          )}
        </div>

        {emCasa ? (
          <p className="rounded bg-info/10 px-3 py-2.5 text-13 text-info"><i className="ri-information-line" aria-hidden /> Recarga em casa não gera lançamento: essa energia já está na sua conta de luz. O valor entra só nas estatísticas do veículo.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Posto (opcional)" value={postoNome} maxLength={120} onChange={(e) => setPostoNome(e.target.value)} />
            <Input label="CNPJ do posto (opcional)" inputMode="numeric" value={postoCnpj} maxLength={18} onChange={(e) => setPostoCnpj(e.target.value)} />
          </div>
        )}

        <div>
          <span className="form-label">Como foi {eletricidade ? 'essa carga' : 'esse abastecimento'}?</span>
          <SeletorAncora cheio={cheio} reserva={reserva} onChange={(c, r) => { setCheio(c); setReserva(r) }}
            rotuloCheio={eletricidade ? 'Carga completa' : 'Enchi o tanque'} rotuloReserva={eletricidade ? 'Bateria na reserva' : 'Estava na reserva'} />
          <p className={cn('mt-2 rounded bg-surface2 px-3 py-2 text-13', previa.tom)}>
            <span className="font-medium">{previa.titulo}.</span> <span className="text-muted">{previa.texto}</span>
          </p>
        </div>

        <div>
          <span className="form-label">Uso desde o último abastecimento (opcional)</span>
          <div className="flex gap-2">
            {Object.values(UsoTrecho).map((u) => (
              <button key={u} type="button" onClick={() => setUso(uso === u ? '' : u)} className={cn('h-[38px] flex-1 rounded text-13 font-medium', uso === u ? 'bg-primary/15 text-primary' : 'bg-surface2 text-muted hover:text-white')}>{USO_TRECHO_LABEL[u]}</button>
            ))}
          </div>
        </div>

        {!emCasa && (
          <div className="space-y-2">
            <span className="form-label">Pagar com</span>
            {editando && (
              <label className="flex items-center gap-2 text-13 text-white">
                <input type="checkbox" checked={trocarPagamento} onChange={(e) => setTrocarPagamento(e.target.checked)} /> Trocar a forma de pagamento
              </label>
            )}
            {trocarPagamento && <PagamentoVeiculoForm valor={pagamento} onChange={setPagamento} />}
            <p className="text-2xs text-muted">Cria o lançamento &quot;{eletricidade ? 'Recarga' : 'Combustível'}&quot; de {formatMoney(total)}, ligado a este abastecimento.</p>
          </div>
        )}

        <Textarea label="Observação (opcional)" rows={2} value={obs} onChange={(e) => setObs(e.target.value)} />
      </fieldset>

      {alerta && (
        <div className="mt-4 rounded bg-warning/10 px-3 py-2.5 text-13 text-warning" role="alert">
          <i className="ri-error-warning-line" aria-hidden /> {alerta.mensagem || 'Esse km parece diferente do esperado.'}
          {alerta.kmAnterior != null && <span> Último km: {numeroBr(alerta.kmAnterior, 0)}.</span>}
          <p className="mt-1 text-xs">Nada foi salvo ainda. Confirme só se o valor estiver certo.</p>
        </div>
      )}
    </Modal>
  )
}
